'use client';

import { Eye, FileText, Loader2, Send } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/app/components/ui/button';
import type { StoryPayload } from '../../types';

interface StoryMessageCardProps {
  story: StoryPayload;
  onOpenDetail?: () => void;
  onSubmitAll?: () => void;
  submitting?: boolean;
  readOnly?: boolean;
}

export const StoryMessageCard = ({
  story,
  onOpenDetail,
  onSubmitAll,
  submitting = false,
  readOnly = false,
}: StoryMessageCardProps) => {
  const t = useTranslations('claw.storyDetail.messageCard');
  const totalDuration = story.shots.reduce((sum, shot) => sum + (shot.durationSec || 0), 0);

  return (
    <div className="rounded-12 border border-system-white08 bg-system-white04 p-4">
      <div className="flex items-center gap-2 text-system-white">
        <FileText className="size-4 text-[#508DFF]" />
        <span className="font-medium text-sm">{story.mdFileName ?? 'story.md'}</span>
        <span className="rounded-full bg-system-white08 px-2 py-0.5 text-system-white48 text-xs">
          {t('parsedBadge')}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-system-white48 text-xs">
        <span>{t('shotCount', { count: story.shots.length })}</span>
        <span>·</span>
        <span>{t('totalDuration', { duration: totalDuration })}</span>
        <span>·</span>
        <span>
          {story.globalConfig.resolution} · {story.globalConfig.aspectRatio}
        </span>
      </div>

      <ul className="mt-3 space-y-1.5">
        {story.shots.slice(0, 3).map((shot, idx) => (
          <li key={shot.id} className="flex items-start gap-2 text-system-white80 text-xs leading-5">
            <span className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-system-white12 text-[10px]">
              {idx + 1}
            </span>
            <span className="line-clamp-1 flex-1">{shot.prompt || t('emptyPrompt')}</span>
            <span className="shrink-0 text-system-white48">{shot.durationSec}s</span>
          </li>
        ))}
        {story.shots.length > 3 ? (
          <li className="text-system-white48 text-xs">{t('moreShots', { count: story.shots.length - 3 })}</li>
        ) : null}
      </ul>

      <div className="mt-4 flex items-center gap-2">
        <Button type="button" variant="secondary" onClick={onOpenDetail}>
          <Eye className="mr-2 size-4" />
          {t('openDetail')}
        </Button>
        {readOnly ? null : (
          <Button type="button" disabled={submitting} onClick={onSubmitAll}>
            {submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Send className="mr-2 size-4" />}
            {submitting ? t('submitting') : t('submit')}
          </Button>
        )}
      </div>
    </div>
  );
};
